import { useEffect, useMemo, useRef, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react';
import type { Card, Catalog } from '@gooflord/shared';
import { CardView } from './Card';

const CRUISE = 38;     // px/s the reel drifts on its own
const MAX_FLING = 2600; // px/s cap on a swipe
const EASE = 1.6;      // how quickly a fling settles back to cruising (per second)
const NUDGE = 900;     // px/s from an arrow key

/** Up to `n` random cards with art for the reel: active, in packs (under level 100), any set or type. */
export function reelCards(cat: Catalog, n = 14): Card[] {
  const pool = Object.values(cat).filter((c) => c.art && !c.inactive && c.level < 100);
  for (let i = pool.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [pool[i], pool[j]] = [pool[j], pool[i]]; }
  return pool.slice(0, n);
}

/**
 * The sign-in page's reel: an endless row of cards drifting sideways. Swipe or drag it to spin it left or right; a fling
 * slows back down to a drift in the direction you threw it. Arrow keys spin it too. With reduced motion it only moves
 * when you swipe. Vertical swipes still scroll the page (touch-action: pan-y).
 */
export function CardCarousel({ cat, label = 'Some of the Gooflord cards' }: { cat: Catalog; label?: string }) {
  const ready = Object.keys(cat).length > 0;
  const cards = useMemo(() => (ready ? reelCards(cat) : []), [ready]); // picked once per visit, when the catalog arrives
  const track = useRef<HTMLDivElement>(null);
  const s = useRef({ x: 0, v: -CRUISE, dir: -1, half: 0, drag: null as null | { id: number; lastX: number; lastT: number; vx: number } });

  useEffect(() => {
    const el = track.current;
    if (!el || !cards.length) return;
    const still = () => matchMedia('(prefers-reduced-motion: reduce)').matches || document.documentElement.classList.contains('reduced-motion');
    const measure = () => { s.current.half = el.scrollWidth / 2; }; // two identical copies: one copy's width is the loop length
    measure();
    const ro = new ResizeObserver(measure); ro.observe(el);
    let raf = 0, last = performance.now();
    const tick = (t: number) => {
      // clamped: a background tab resumes without a jump, and the first frame's timestamp can predate `last`
      const dt = Math.max(0, Math.min(0.05, (t - last) / 1000)); last = t;
      const st = s.current;
      if (!st.drag) {
        const target = still() ? 0 : st.dir * CRUISE;
        st.v += (target - st.v) * Math.min(1, dt * EASE);
        st.x += st.v * dt;
      }
      if (st.half) { st.x %= st.half; if (st.x > 0) st.x -= st.half; } // keep x in (-half, 0] so the loop is seamless
      el.style.transform = `translate3d(${st.x}px, 0, 0)`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [cards]);

  const down = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* pointer already gone: dragging still works without capture */ }
    s.current.drag = { id: e.pointerId, lastX: e.clientX, lastT: performance.now(), vx: 0 };
    s.current.v = 0;
  };
  const move = (e: PointerEvent<HTMLDivElement>) => {
    const d = s.current.drag;
    if (!d || d.id !== e.pointerId) return;
    const now = performance.now(), dx = e.clientX - d.lastX, dt = Math.max(1, now - d.lastT) / 1000;
    s.current.x += dx;
    d.vx = d.vx * 0.6 + (dx / dt) * 0.4; // smoothed, so the release speed isn't one jittery sample
    d.lastX = e.clientX; d.lastT = now;
  };
  const up = (e: PointerEvent<HTMLDivElement>, cancelled = false) => {
    const st = s.current, d = st.drag;
    if (!d || d.id !== e.pointerId) return;
    st.drag = null;
    const idle = performance.now() - d.lastT > 120; // held still before letting go: no fling
    const vx = cancelled || idle ? 0 : Math.max(-MAX_FLING, Math.min(MAX_FLING, d.vx));
    st.v = vx;
    if (Math.abs(vx) > 60) st.dir = Math.sign(vx);
  };
  const key = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const dir = e.key === 'ArrowLeft' ? -1 : 1;
    s.current.dir = dir; s.current.v = dir * NUDGE;
  };

  if (!cards.length) return <div className="reel" aria-hidden="true" />;
  const row = (copy: number) => cards.map((c, i) => (
    <div key={`${copy}-${c.slug}`} className="reel-item" style={{ '--tilt': `${((i * 7) % 5) - 2}deg` } as CSSProperties} aria-hidden={copy ? true : undefined}>
      <CardView card={c} />
    </div>
  ));
  return (
    <div className="reel" role="region" aria-roledescription="carousel" aria-label={`${label}. Swipe or use the arrow keys to spin it.`} tabIndex={0}
      onPointerDown={down} onPointerMove={move} onPointerUp={(e) => up(e)} onPointerCancel={(e) => up(e, true)} onKeyDown={key}>
      <div className="reel-track" ref={track}>{row(0)}{row(1)}</div>
    </div>
  );
}
